/**
 * Age Groups (Related) — the dashboard detail page for the
 * /report-management/descendants-by-age-category report.
 *
 * Laid out like Males/Females: two columns side by side, "17 and Younger" and
 * "18 and Older", each titled with its count and listing one row per member —
 * age, name, then the head of their line and its colour. The age leads the
 * row where Males/Females prints the family-line number: on an age report
 * the years are what the reader is scanning for, so they take the first
 * column, right-aligned so 5 and 55 line up. A child under a year old
 * prints the months — "5 mos." — or, within the first month, the days —
 * "12 days" — never a bare 0 (see getMemberAgeLabel). A member whose age is
 * unknown leaves the column blank. The split is the endpoint's own: two
 * groups, each with its own count, which the column titles print and the
 * dashboard card prints as a pair.
 *
 * /dashboard-statistics-with-details is read for what the report rows do not
 * carry: the photo, the name markers (the multiple-birth tag, the deceased
 * angel; the in-law asterisk never applies here but rides along in the
 * shared row), the date of birth the two age sorts read, and the heads of line the
 * colour legend and the row's line name are built from. A row the roster
 * does not know prints off the report row alone.
 *
 * Sorts — A–Z, Z–A, Line Color, Oldest, Youngest — then Living and Deceased
 * tabs. The page opens on everyone, living and deceased, with neither tab
 * lit; Living or Deceased narrows both columns to those rows, and picking the
 * lit tab again goes back to everyone. Line Color groups each column's rows by line and reads each
 * line oldest to youngest. Opens oldest first in each column with nothing lit, as
 * Males/Females opens. The search box reads the name, the age and the line
 * — colour and head — of every row: "gold" keeps the Gold Line, "45" keeps
 * everyone aged 45 (an age is matched whole, so 5 never finds 15 and 50),
 * and "smith 45" narrows to both. An Age Range dropdown at the head of the
 * bar's row — From and To in years, either bound optional, Clear and Apply
 * under them, as on Family Gatherings — narrows the columns by age. The
 * range never moves a member across the split: "5–30" keeps the 5–17s in
 * the left column and the 18–30s in the right. The age is the report row's
 * own, else counted off the roster's date of birth (see
 * getDescAgeGroupMemberAge). A Gender dropdown beside it — Male, Female,
 * and Other when any row is Binary or Other — narrows both columns the same
 * way; the gender is the record's own, else the roster's Males/Females lists
 * (see getDescAgeGroupMemberGender).
 */

import React, {
  useCallback,
  useEffect,
  useState,
  useMemo,
  useRef,
  useLayoutEffect,
} from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import { getMemberAgeLabel } from "../../../Utils/family-tree/dateAndTime";
import { isExplicitDashboardStartOrTopOfLineMember } from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  getDashboardMemberNameParts,
  isDashboardInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import useDashboardPhoneLayout from "./useDashboardPhoneLayout";
import {
  getDashboardMemberSearchText,
  normalizeDashboardSearchText,
} from "../../../Utils/dashboardDetailSearch";
import {
  compareDashboardMembersByOldest,
  compareDashboardMembersByYoungest,
} from "../../../Utils/dashboardFamilyLineNumbers";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import {
  DESC_AGE_GROUP_GENDERS,
  fetchDescendantsByAgeCategory,
  filterDescAgeGroupMembersByAge,
  getDescAgeGroupMemberAge,
  getDescAgeGroupMemberGender,
} from "../../../Utils/dashboardDescAgeGroup";
import { buildGenderMap } from "../../../Utils/dashboardMultiBirths";
import {
  DASHBOARD_MEMBER_FILTERS,
  filterDashboardMembersByMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { FilterTriggerLabel, filterTriggerClass } from "../../../Utils/filterTriggerLabel";

/** The order each column opens in; nothing is lit for it, as on Males/Females. */
const DEFAULT_SORT = "oldest";

/** The page opens on everyone, and falls back to it when the lit status tab is picked again. */
const DEFAULT_MEMBER_FILTER = DASHBOARD_MEMBER_FILTERS.ALL;

/** Column titles — the report's own two brackets, in the words the card uses. */
const MINORS_TITLE = "17 and Younger";
const ADULTS_TITLE = "18 and Older";

/** The Gender dropdown's choices, in the order they are listed. */
const GENDER_OPTIONS = [
  { value: DESC_AGE_GROUP_GENDERS.MALE, label: "Male" },
  { value: DESC_AGE_GROUP_GENDERS.FEMALE, label: "Female" },
  { value: DESC_AGE_GROUP_GENDERS.OTHER, label: "Other" },
];

const compareNames = (nameA, nameB) =>
  nameA.toLowerCase().localeCompare(nameB.toLowerCase());

/** A typed age → whole years, or null for blank / not a number. */
const parseAgeInput = (value) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return null;
  const parsed = parseInt(trimmed, 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
};

const recordId = (record) => {
  const id = record?.id ?? record?.member_id;
  return id == null || id === "" ? "" : String(id);
};

function DescendantAgeGroup() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  // Full roster, kept to build the colour legend and to fill in each report
  // row's record — see the extractor and resolveMember.
  const [legendMembers, setLegendMembers] = useState([]);
  const [rosterById, setRosterById] = useState(() => new Map());
  // id → "M"/"F" off the roster's Males/Females lists, for rows whose record
  // names no gender of its own.
  const [genderById, setGenderById] = useState(() => new Map());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isLargeView] = useState(false);
  const measureListRef = useRef(null);
  const [nameColumnWidth, setNameColumnWidth] = useState(null);
  const isCompactLayout = useDashboardPhoneLayout();
  // The Age Range dropdown: the years being typed, the range in force, and
  // whether the panel is open. Typing only takes hold on Apply, as on Family
  // Gatherings, and a From above the To is refused with a note in the panel.
  const [showAgeRange, setShowAgeRange] = useState(false);
  const [ageFromInput, setAgeFromInput] = useState("");
  const [ageToInput, setAgeToInput] = useState("");
  const [appliedAgeRange, setAppliedAgeRange] = useState(null); // { from, to } | null
  const [ageRangeError, setAgeRangeError] = useState("");
  const ageRangeRef = useRef(null);
  // The Gender dropdown: the choice being picked, the one in force ("" for
  // everyone), and whether the panel is open. As with Age Range, a pick only
  // takes hold on Apply.
  const [showGender, setShowGender] = useState(false);
  const [selectedGender, setSelectedGender] = useState("");
  const [appliedGender, setAppliedGender] = useState("");
  const genderRef = useRef(null);
  // The search + filter block is centred on the column divider, not the page:
  // the columns are only as wide as their names, so the line moves with them.
  const controlsRef = useRef(null);
  const columnsWrapperRef = useRef(null);
  const columnDividerRef = useRef(null);

  // Close the Age Range and Gender panels on an outside click
  useEffect(() => {
    const onDocClick = (e) => {
      if (ageRangeRef.current && !ageRangeRef.current.contains(e.target)) {
        setShowAgeRange(false);
      }
      if (genderRef.current && !genderRef.current.contains(e.target)) {
        setShowGender(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // The report is the page; the roster only dresses its rows, so the two land
  // on their own and a roster call that failed still leaves the list on screen.
  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();
        const nextReport = await fetchDescendantsByAgeCategory(family_tree_id);
        if (!cancelled) setReport(nextReport);
      } catch (error) {
        console.error("Error fetching descendants by age category:", error);
        if (!cancelled) setReport(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchReport();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const fetchRoster = async () => {
      try {
        const family_tree_id = getFamilyTreeIdFromStorage();
        if (!family_tree_id) return;

        const response = await getApi(
          `/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`
        );
        if (response?.statusCode !== 200 || !response?.data) return;

        const members = extractAllMembersFromStatisticsResponse(response.data);
        setHeadsMap(
          buildHeadsOfLineIdByColorMap(
            extractHeadsOfLineFromStatisticsResponse(response.data),
            members
          )
        );
        setLegendMembers(members);
        setGenderById(buildGenderMap(response.data, []));
        setRosterById(
          new Map(
            members
              .map((member) => [recordId(member), member])
              .filter(([id]) => id)
          )
        );
      } catch (error) {
        console.error("Error fetching family roster:", error);
      }
    };

    fetchRoster();
  }, []);

  /**
   * The record a row is printed from: the report row under its roster record,
   * so the roster's photo, date of birth and markers win and the report's own
   * fields (name parts, line colour, head of line) fill whatever the roster
   * leaves blank. The age is the exception: the report's `age` always wins. A row the roster does not know reads as living, which is all
   * the endpoint returns, and its colour under whichever key the row spells it.
   */
  const resolveMember = useCallback(
    (row) => {
      const roster = rosterById.get(recordId(row));
      const merged = { present_or_deceased: 1, ...row, ...(roster || {}) };
      // The age is the report's own — the number it split the columns by —
      // never the roster's, which can read a year apart.
      if (row?.age != null && row.age !== "") merged.age = row.age;
      if (!merged.color_name && (row?.color || row?.family_line_color)) {
        merged.color_name = row.color ?? row.family_line_color;
      }
      if (!merged.hex_code && row?.family_line_hex_code) {
        merged.hex_code = row.family_line_hex_code;
      }
      return merged;
    },
    [rosterById]
  );

  const minors = useMemo(
    () => (report?.minors?.members || []).map(resolveMember),
    [report, resolveMember]
  );

  const adults = useMemo(
    () => (report?.adults?.members || []).map(resolveMember),
    [report, resolveMember]
  );

  const [sortBy, setSortBy] = useState("none"); // "none", "alpha-asc", "alpha-desc", "youngest", "oldest", "color"
  const [searchQuery, setSearchQuery] = useState("");
  const [memberFilter, setMemberFilter] = useState(DEFAULT_MEMBER_FILTER);

  // Living / Deceased, or everyone with neither lit; the sort in force carries over.
  const toggleMemberFilter = useCallback((filter) => {
    setMemberFilter((current) => (current === filter ? DEFAULT_MEMBER_FILTER : filter));
  }, []);

  // What the list is actually ordered by: with nothing lit, oldest first.
  const effectiveSort = sortBy === "none" ? DEFAULT_SORT : sortBy;

  const formatPersonName = (member) =>
    formatDashboardMemberDisplayName(member, { appendDescendantSpouse: false });

  const searchTerms = useMemo(
    () => normalizeDashboardSearchText(searchQuery).split(" ").filter(Boolean),
    [searchQuery]
  );

  /**
   * True when every word typed turns up on the row: in the name (the printed
   * name and the record's own name parts), in the line — its colour or its
   * head — or as the age. A number is matched against the age whole, so "5"
   * finds the five-year-olds and not everyone aged 15 or 50; a word is
   * matched anywhere, so "mos" finds the infants and "gold" the Gold Line.
   */
  const matchesSearch = useCallback(
    (member) => {
      if (!searchTerms.length) return true;

      const nameText = normalizeDashboardSearchText(
        getDashboardMemberSearchText(member, formatPersonName(member))
      );
      const lineText = normalizeDashboardSearchText(
        [
          member?.color_name ? `${member.color_name} line` : "",
          getDashboardFamilyLineHeadDisplayName(member, headsMap),
        ].join(" ")
      );
      const age = getDescAgeGroupMemberAge(member);
      const ageText = normalizeDashboardSearchText(getMemberAgeLabel(member, age) ?? "");
      const ageTokens = ageText ? ageText.split(" ") : [];
      if (age != null) ageTokens.push(String(age));

      return searchTerms.every((term) =>
        /^\d+$/.test(term)
          ? ageTokens.includes(term)
          : nameText.includes(term) || lineText.includes(term) || ageText.includes(term)
      );
    },
    [searchTerms, headsMap]
  );

  const getMemberGender = useCallback(
    (member) => getDescAgeGroupMemberGender(member, genderById),
    [genderById]
  );

  // The Living/Deceased tab picks the rows, the age range and gender narrow
  // each column, then the search box narrows it again, so both column counts
  // and the heading read the rows on screen.
  const filterColumn = useCallback(
    (members) =>
      filterDescAgeGroupMembersByAge(
        filterDashboardMembersByMemberFilter(members, memberFilter),
        appliedAgeRange || {}
      )
        .filter((member) => !appliedGender || getMemberGender(member) === appliedGender)
        .filter(matchesSearch),
    [memberFilter, appliedAgeRange, appliedGender, getMemberGender, matchesSearch]
  );

  const filteredMinors = useMemo(() => filterColumn(minors), [minors, filterColumn]);
  const filteredAdults = useMemo(() => filterColumn(adults), [adults, filterColumn]);

  // Other is only offered when some row is Binary or Other, or it is in force.
  const genderOptions = useMemo(() => {
    const hasOther = [...minors, ...adults].some(
      (member) => getMemberGender(member) === DESC_AGE_GROUP_GENDERS.OTHER
    );
    return GENDER_OPTIONS.filter(
      (option) =>
        option.value !== DESC_AGE_GROUP_GENDERS.OTHER ||
        hasOther ||
        appliedGender === DESC_AGE_GROUP_GENDERS.OTHER
    );
  }, [minors, adults, getMemberGender, appliedGender]);

  const genderFilterValue =
    GENDER_OPTIONS.find((option) => option.value === appliedGender)?.label ?? "";

  const openGender = () => {
    // The panel reopens on the choice in force, not on one never applied.
    setSelectedGender(appliedGender);
    setShowGender((prev) => !prev);
  };

  const applyGender = () => {
    setAppliedGender(selectedGender);
    setShowGender(false);
  };

  const clearGender = () => {
    setSelectedGender("");
    setAppliedGender("");
    setShowGender(false);
  };

  const openAgeRange = () => {
    // The panel reopens on the range in force, not on whatever was last typed
    // and never applied.
    setAgeFromInput(appliedAgeRange?.from != null ? String(appliedAgeRange.from) : "");
    setAgeToInput(appliedAgeRange?.to != null ? String(appliedAgeRange.to) : "");
    setAgeRangeError("");
    setShowAgeRange((prev) => !prev);
  };

  const applyAgeRange = () => {
    const from = parseAgeInput(ageFromInput);
    const to = parseAgeInput(ageToInput);
    if (ageFromInput.trim() !== "" && from == null) {
      setAgeRangeError("From must be a whole number of years.");
      return;
    }
    if (ageToInput.trim() !== "" && to == null) {
      setAgeRangeError("To must be a whole number of years.");
      return;
    }
    if (from != null && to != null && to < from) {
      setAgeRangeError("To must be the same as From or older.");
      return;
    }
    setAppliedAgeRange(from == null && to == null ? null : { from, to });
    setAgeRangeError("");
    setShowAgeRange(false);
  };

  const clearAgeRange = () => {
    setAgeFromInput("");
    setAgeToInput("");
    setAppliedAgeRange(null);
    setAgeRangeError("");
    setShowAgeRange(false);
  };

  // "5–17", "18+" or "up to 12" — what the trigger reads while a range is on;
  // word joiners keep it on one line when the trigger wraps.
  const ageRangeFilterValue = !appliedAgeRange
    ? ""
    : appliedAgeRange.from != null && appliedAgeRange.to != null
      ? `${appliedAgeRange.from}\u2060–\u2060${appliedAgeRange.to}`
      : appliedAgeRange.from != null
        ? `${appliedAgeRange.from}+`
        : `up\u00a0to\u00a0${appliedAgeRange.to}`;

  const sortMembers = useCallback((members) => {
    if (!members.length) return members;

    if (effectiveSort === "alpha-asc" || effectiveSort === "alpha-desc") {
      const direction = effectiveSort === "alpha-asc" ? 1 : -1;
      return [...members].sort(
        (a, b) => direction * compareNames(formatPersonName(a), formatPersonName(b))
      );
    }

    if (effectiveSort === "youngest") {
      return [...members].sort(compareDashboardMembersByYoungest);
    }

    if (effectiveSort === "color") {
      // Grouped by line colour, each line read A–Z by name.
      return [...members].sort((a, b) => {
        const colorA = (a?.color_name || "").toLowerCase();
        const colorB = (b?.color_name || "").toLowerCase();
        if (colorA === colorB) {
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          return nameA.localeCompare(nameB) || compareDashboardMembersByOldest(a, b);
        }
        return colorA.localeCompare(colorB);
      });
    }

    return [...members].sort(compareDashboardMembersByOldest);
  }, [effectiveSort]);

  const sortedMinors = useMemo(() => sortMembers(filteredMinors), [filteredMinors, sortMembers]);
  const sortedAdults = useMemo(() => sortMembers(filteredAdults), [filteredAdults, sortMembers]);

  // The age column is dropped from a column where no row has an age to show
  const showMinorAgeColumn = useMemo(
    () => sortedMinors.some((member) => getDescAgeGroupMemberAge(member) != null),
    [sortedMinors]
  );

  const showAdultAgeColumn = useMemo(
    () => sortedAdults.some((member) => getDescAgeGroupMemberAge(member) != null),
    [sortedAdults]
  );

  useLayoutEffect(() => {
    if (loading || (sortedMinors.length === 0 && sortedAdults.length === 0)) {
      setNameColumnWidth(null);
      return;
    }

    const measureEl = measureListRef.current;
    if (!measureEl) return;

    const measureNameColumnWidth = () => {
      const nameCols = measureEl.querySelectorAll(".dashboard-detail-name-col");
      let maxWidth = 0;
      nameCols.forEach((col) => {
        maxWidth = Math.max(maxWidth, col.getBoundingClientRect().width);
      });
      if (maxWidth > 0) {
        setNameColumnWidth(Math.ceil(maxWidth));
      }
    };

    measureNameColumnWidth();

    const ro = new ResizeObserver(measureNameColumnWidth);
    ro.observe(measureEl);
    window.addEventListener("resize", measureNameColumnWidth);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureNameColumnWidth);
    };
  }, [
    sortedMinors,
    sortedAdults,
    loading,
    isLargeView,
    sortBy,
    showMinorAgeColumn,
    showAdultAgeColumn,
  ]);

  // Desktop only: on a phone the controls wrapper is display: contents. The
  // block is centred in its line first (the CSS default) to read its natural
  // width, then shifted so its centre sits over the divider, clamped so it
  // never runs past either edge of the line.
  useLayoutEffect(() => {
    const controlsEl = controlsRef.current;
    if (!controlsEl) return;

    const resetControls = () => {
      controlsEl.style.justifyContent = "";
      controlsEl.style.paddingLeft = "";
    };

    const centreControlsOnDivider = () => {
      resetControls();
      const dividerEl = columnDividerRef.current;
      if (!dividerEl || !window.matchMedia("(min-width: 769px)").matches) return;

      const controlsRect = controlsEl.getBoundingClientRect();
      const blockWidth = Math.max(
        ...Array.from(controlsEl.children).map((child) => child.getBoundingClientRect().width)
      );
      const dividerRect = dividerEl.getBoundingClientRect();
      const dividerCentre = dividerRect.left + dividerRect.width / 2 - controlsRect.left;
      const maxOffset = Math.max(0, controlsRect.width - blockWidth);
      const offset = Math.min(maxOffset, Math.max(0, dividerCentre - blockWidth / 2));

      controlsEl.style.justifyContent = "start";
      controlsEl.style.paddingLeft = `${offset}px`;
    };

    centreControlsOnDivider();

    const ro = new ResizeObserver(centreControlsOnDivider);
    if (columnsWrapperRef.current) ro.observe(columnsWrapperRef.current, { box: "border-box" });
    window.addEventListener("resize", centreControlsOnDivider);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", centreControlsOnDivider);
      resetControls();
    };
  }, [loading, sortedMinors, sortedAdults, nameColumnWidth, isLargeView, searchQuery]);

  // `applyMeasuredWidth` is false for the hidden measuring list only: feeding the
  // measured width back into the rows it is measured from would latch the column
  // at whatever the first search needed, so a later one with longer names could
  // never widen it.
  const renderMemberRow = (member, showAgeColumn, applyMeasuredWidth = true) => {
    const { baseName, descendantSpouseName } = getDashboardMemberNameParts(member, {
      appendDescendantSpouse: false,
    });
    const ageLabel = getMemberAgeLabel(member, getDescAgeGroupMemberAge(member));
    const isDeceased = member?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    const showInLawAsterisk =
      isDashboardInLawMember(member) && !isExplicitDashboardStartOrTopOfLineMember(member);
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const hexCode = member?.hex_code || "#000000";
    const colorName = member?.color_name || "";

    const iconSize = isLargeView ? "18px" : "12px";

    return (
      <div className={`dashboard-detail-row ${isLargeView ? "large-view" : ""}`}>
        <div
          className={`dashboard-detail-name-col ${isLargeView ? "large-view" : ""}${
            !showAgeColumn ? " dashboard-detail-name-col--no-line-number" : ""
          }`}
          style={
            nameColumnWidth && applyMeasuredWidth
              ? {
                  width: `${nameColumnWidth}px`,
                  minWidth: `${nameColumnWidth}px`,
                }
              : undefined
          }
        >
          {showAgeColumn && (
            <span
              className="dashboard-family-line-number descendant-age-group-age"
              aria-hidden={ageLabel == null}
            >
              {ageLabel ?? ""}
            </span>
          )}
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...getDashboardMemberNamePhotoUrls(member)}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={baseName}
                descendantSpouseName={descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && descendantSpouseName ? (
                    <DashboardInLawAsterisk className="total-male-female-page in-law-asterisk" isLargeView={isLargeView} />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !descendantSpouseName && (
              <DashboardInLawAsterisk className="total-male-female-page in-law-asterisk" isLargeView={isLargeView} />
            )}
            {isDeceased && (
              <span className={`dashboard-detail-angel-icon ${isLargeView ? "large-view" : ""}`}>
                <img
                  src={window.getPublicUrl("/assets/images/angel.png")}
                  alt="deceased"
                  style={{ width: iconSize, height: iconSize }}
                />
              </span>
            )}
            {multipleBirthSup && (
              <sup
                className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}
              >
                {multipleBirthSup}
              </sup>
            )}
          </DashboardNameMarkerGroup>
        </div>

        {/* Compact drops the head-of-line name and prints the line colour in its own
            colour; the footer legend ties each colour to its head */}
        {isCompactLayout ? (
          <div className="dashboard-detail-line-col dashboard-detail-line-col--compact">
            <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
          </div>
        ) : (
          <div
            className={`dashboard-detail-line-col ${isLargeView ? "large-view-text" : ""}`}
          >
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
              onViewPhoto={openPhoto}
              member={member}
              headsMap={headsMap}
            />
          </div>
        )}
      </div>
    );
  };

  const rawTotalCount = minors.length + adults.length;
  const totalCount = filteredMinors.length + filteredAdults.length;
  const minorCount = filteredMinors.length;
  const adultCount = filteredAdults.length;

  // Built from the whole tree, not this page's rows, so the footer legend lists the
  // same family lines here as on every other dashboard list.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(legendMembers, headsMap),
    [legendMembers, headsMap]
  );

  const showColorLegend = isCompactLayout && legendLines.length > 0;

  const ageRangeDropdown = (
    <div
      ref={ageRangeRef}
      className="selctions-option position-relative descendant-age-group-filter"
    >
      <div
        className={filterTriggerClass(
          "selection-drop descendant-age-group-filter-trigger",
          Boolean(ageRangeFilterValue)
        )}
        style={{ fontWeight: "bold" }}
        onClick={(e) => {
          e.stopPropagation();
          openAgeRange();
        }}
      >
        {/* The phone trigger is too narrow for "Age Range: 10–30", so once a
            range is on it reads "Age: 10–30" — the value is what matters there */}
        <FilterTriggerLabel
          label={isCompactLayout && ageRangeFilterValue ? "Age" : "Age Range"}
          value={ageRangeFilterValue}
        />
      </div>
      {showAgeRange && (
        <div
          className="member-list descendant-age-group-filter-list"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="descendant-age-group-filter-fields">
            <label className="descendant-age-group-filter-field">
              <span>From</span>
              <input
                type="number"
                inputMode="numeric"
                min="0"
                placeholder="Age"
                value={ageFromInput}
                onChange={(e) => {
                  setAgeFromInput(e.target.value);
                  setAgeRangeError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && applyAgeRange()}
              />
            </label>
            <label className="descendant-age-group-filter-field">
              <span>To</span>
              <input
                type="number"
                inputMode="numeric"
                min={parseAgeInput(ageFromInput) ?? 0}
                placeholder="Age"
                value={ageToInput}
                onChange={(e) => {
                  setAgeToInput(e.target.value);
                  setAgeRangeError("");
                }}
                onKeyDown={(e) => e.key === "Enter" && applyAgeRange()}
              />
            </label>
          </div>
          {ageRangeError && (
            <p className="descendant-age-group-filter-error" role="alert">
              {ageRangeError}
            </p>
          )}
          <div className="btn-container">
            <button type="button" className="btn btn-clear" onClick={clearAgeRange}>
              Clear
            </button>
            <button type="button" className="btn btn-apply" onClick={applyAgeRange}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const genderDropdown = (
    <div
      ref={genderRef}
      className="selctions-option position-relative descendant-age-group-filter"
    >
      <div
        className={filterTriggerClass(
          "selection-drop descendant-age-group-filter-trigger descendant-age-group-gender-trigger",
          Boolean(genderFilterValue)
        )}
        style={{ fontWeight: "bold" }}
        onClick={(e) => {
          e.stopPropagation();
          openGender();
        }}
      >
        <FilterTriggerLabel label="Gender" value={genderFilterValue} />
      </div>
      {showGender && (
        <div
          className="member-list descendant-age-group-filter-list descendant-age-group-gender-list"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="descendant-age-group-gender-options" role="radiogroup">
            {[{ value: "", label: "All" }, ...genderOptions].map((option) => (
              <label key={option.value || "all"} className="descendant-age-group-gender-option">
                <input
                  type="radio"
                  name="descendant-age-group-gender"
                  checked={selectedGender === option.value}
                  onChange={() => setSelectedGender(option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
          <div className="btn-container">
            <button type="button" className="btn btn-clear" onClick={clearGender}>
              Clear
            </button>
            <button type="button" className="btn btn-apply" onClick={applyGender}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );

  // "living female ", "deceased ", "" — the words the empty state puts before
  // "family members" for the tab and gender in force.
  const emptyStateQualifier = [
    memberFilter === DASHBOARD_MEMBER_FILTERS.DECEASED
      ? "deceased"
      : memberFilter === DASHBOARD_MEMBER_FILTERS.LIVING
        ? "living"
        : "",
    genderFilterValue.toLowerCase(),
  ]
    .filter(Boolean)
    .map((word) => `${word} `)
    .join("");

  return (
    <div
      className={`dashboard-container total-male-female-page descendant-age-group-page dashboard-detail-page ${
        isLargeView ? "large-view-mode" : ""
      }`}
    >
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
            <DashboardBackButton />
            <div className="dashboard-detail-heading-label-cluster">
              <h3 className="dashboard-detail-title">Age Groups (Related)</h3>
              <div className="dashboard-detail-count">{totalCount}</div>
            </div>
            {/* Search and filters share one block: on a desktop the search
                runs the width of the filter row and the pair is centred over
                both columns. */}
            {!loading && rawTotalCount > 0 && (
              <div ref={controlsRef} className="descendant-age-group-controls">
                <DashboardDetailSearchBox
                  centreOverDivider={false}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder="Search name, age or line color"
                  ariaLabel="Search by name, age or line color"
                />
                {/* The Age Range and Gender dropdowns lead the bar's row, left of A–Z, in
                    a row of its own so it always lands there whatever the
                    header wraps — and outside the bar itself, which clips
                    anything that opens past its edge. */}
                <div className="descendant-age-group-filter-row">
                  {ageRangeDropdown}
                  {genderDropdown}
                  <DashboardDetailSortFilterBar
                    className="dashboard-detail-sort-toggle total-male-female-sort-toggle"
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                    defaultSortByMemberFilter={{
                      [DASHBOARD_MEMBER_FILTERS.ALL]: DEFAULT_SORT,
                      [DASHBOARD_MEMBER_FILTERS.LIVING]: DEFAULT_SORT,
                      [DASHBOARD_MEMBER_FILTERS.DECEASED]: DEFAULT_SORT,
                    }}
                    showMemberFilters={false}
                    showLivingDeceasedFilters
                    memberFilter={memberFilter}
                    onMemberFilterToggle={toggleMemberFilter}
                    />
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading family members...</p>
          </div>
        ) : rawTotalCount === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No data for this period.</p>
          </div>
        ) : totalCount === 0 ? (
          <div className="dashboard-detail-empty-state">
            {searchQuery.trim() ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : appliedAgeRange ? (
              <p>
                No {emptyStateQualifier}family members aged {ageRangeFilterValue}.
              </p>
            ) : emptyStateQualifier ? (
              <p>No {emptyStateQualifier}family members.</p>
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div className="total-male-female-page dashboard-detail-content-wrapper">
            <div ref={columnsWrapperRef} className="total-male-female-page gender-columns-wrapper">
              <div
                ref={measureListRef}
                className="total-male-female-page gender-list-measure"
                aria-hidden="true"
              >
                {sortedMinors.map((member, index) => (
                  <div key={`measure-minor-${member?.id || index}`}>
                    {renderMemberRow(member, showMinorAgeColumn, false)}
                  </div>
                ))}
                {sortedAdults.map((member, index) => (
                  <div key={`measure-adult-${member?.id || index}`}>
                    {renderMemberRow(member, showAdultAgeColumn, false)}
                  </div>
                ))}
              </div>
              {/* 17 and Younger column */}
              <div className="total-male-female-page gender-column gender-column-male">
                <h4 className="total-male-female-page gender-column-title minors dashboard-section-heading">
                  {MINORS_TITLE}{" "}<span className="dashboard-section-heading-count">({minorCount})</span>
                </h4>
                <div className="total-male-female-page gender-list-cluster">
                  <div className="total-male-female-page gender-list">
                    {sortedMinors.length === 0 ? (
                      <p className="total-male-female-page gender-empty-text">No data available.</p>
                    ) : (
                      sortedMinors.map((member, index) => (
                        <React.Fragment key={member?.id || index}>
                          {renderMemberRow(member, showMinorAgeColumn)}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Vertical Divider */}
              <div ref={columnDividerRef} className="total-male-female-page gender-column-divider"></div>

              {/* 18 and Older column */}
              <div className="total-male-female-page gender-column">
                <h4 className="total-male-female-page gender-column-title adults dashboard-section-heading">
                  {ADULTS_TITLE}{" "}<span className="dashboard-section-heading-count">({adultCount})</span>
                </h4>
                <div className="total-male-female-page gender-list-cluster">
                  <div className="total-male-female-page gender-list">
                    {sortedAdults.length === 0 ? (
                      <p className="total-male-female-page gender-empty-text">No data available.</p>
                    ) : (
                      sortedAdults.map((member, index) => (
                        <React.Fragment key={member?.id || index}>
                          {renderMemberRow(member, showAdultAgeColumn)}
                        </React.Fragment>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="total-male-female-page in-law-footer">
              {/* One footer under both columns, so the key covers the tags in either */}
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags([
                  sortedMinors,
                  sortedAdults,
                ])}
              />
              {showColorLegend && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
          </div>
        )}
      </div>
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default DescendantAgeGroup;
